import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext';
import { API_BASE_URL } from '../../../../constants';

const CloudDecks = ({ currentDeckData, onLoadDeck }) => {
    const { t } = useTranslation();
    const { user, isAuthenticated, loading: authLoading, token } = useAuth();

    const [decks, setDecks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [deleteCandidate, setDeleteCandidate] = useState(null);

    const fetchDecks = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/decks`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setDecks(data);
            } else {
                if (response.status === 401) {
                    setFeedbackMsg("Session expired. Please login again.");
                }
            }
        } catch (error) {
            console.error("Failed to fetch decks", error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Fetch decks when expanded and user exists
    useEffect(() => {
        if (isAuthenticated && isExpanded) {
            fetchDecks();
        }
    }, [isAuthenticated, isExpanded, fetchDecks]);

    const handleBannerClick = () => {
        if (authLoading) return;

        if (!isAuthenticated) {
            setShowLoginModal(true);
        } else {
            setIsExpanded(!isExpanded);
        }
    };

    const handleSave = async (overwriteId = null) => {
                const payload = {
            data: currentDeckData
        };

        if (!overwriteId && !saveName.trim()) {
            setFeedbackMsg(t('CloudDecks.msg_enter_name'));
            setTimeout(() => setFeedbackMsg(''), 3000);
            return;
        }

        if (!overwriteId) {
            payload.name = saveName;
        }

        const url = overwriteId
            ? `${API_BASE_URL}/api/deck/${overwriteId}`
            : `${API_BASE_URL}/api/deck`;

        const method = overwriteId ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setSaveName('');
                fetchDecks();
                setFeedbackMsg(overwriteId ? t('CloudDecks.msg_updated') : t('CloudDecks.msg_saved'));
            } else {
                const err = await response.json();
                setFeedbackMsg(err.error || t('CloudDecks.msg_error_saving'));
            }
        } catch (e) {
            setFeedbackMsg(t('CloudDecks.msg_network_error'));
        }
        setTimeout(() => setFeedbackMsg(''), 3000);
    };

    const handleDeleteClick = (deck) => {
        setDeleteCandidate(deck);
    };

    const confirmDelete = async () => {
        if (!deleteCandidate) return;

                try {
            const response = await fetch(`${API_BASE_URL}/api/deck/${deleteCandidate.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchDecks();
                setFeedbackMsg(t('CloudDecks.msg_deleted', { defaultValue: 'Deck deleted' }));
            }
        } catch (e) {
            console.error("Delete failed", e);
        } finally {
            setDeleteCandidate(null);
        }
    };

    const DeckIcon = ({ id }) => (
        <div className="w-8 h-8 rounded overflow-hidden border border-slate-300 dark:border-slate-600 bg-slate-200">
            {id ? (
                <img
                    src={`${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${id}_Icon.png&width=64`}
                    alt="Card"
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">?</div>
            )}
        </div>
    );

    return (
        <div className="w-full mb-6 relative z-20">
            {/* Banner */}
            <button
                onClick={handleBannerClick}
                disabled={authLoading}
                className={`w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl p-3 shadow-lg flex items-center justify-between transition-all ${authLoading ? 'opacity-75 cursor-wait' : ''}`}
            >
                <div className="flex items-center gap-3">
                    {authLoading ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                    )}
                    <span className="font-bold text-lg">
                        {authLoading ? t('CloudDecks.verifying_session', { defaultValue: 'Verifying...' }) : t('CloudDecks.banner_title')}
                    </span>
                </div>
                {isExpanded ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                )}
            </button>

            {/* Login Modal */}
            {showLoginModal && (
                <div className="absolute top-14 left-0 w-full bg-slate-800 text-white p-6 rounded-xl shadow-2xl border border-orange-500 z-50 animate-in fade-in slide-in-from-top-4">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-orange-400">{t('CloudDecks.login_required_title')}</h3>
                        <button onClick={() => setShowLoginModal(false)} className="text-slate-400 hover:text-white">✕</button>
                    </div>
                    <p className="mb-4 text-slate-300">{t('CloudDecks.login_required_desc')}</p>
                    <div className="flex gap-2 justify-end">
                        <a href="/login" className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded text-white font-bold text-sm">{t('CloudDecks.btn_login')}</a>
                        <button onClick={() => setShowLoginModal(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm">{t('CloudDecks.btn_close')}</button>
                    </div>
                </div>
            )}

            {/* Implementation Panel */}
            {isAuthenticated && isExpanded && (
                <div className="mt-2 text-left bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 relative">

                    {/* Header Status */}
                    <div className="bg-slate-100 dark:bg-slate-900/50 p-3 flex justify-between items-center text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex gap-4">
                            <span>{user?.name}</span>
                            <span>{t('CloudDecks.status_slots', { count: decks.length })}</span>
                        </div>
                        <span>{feedbackMsg && <span className="text-green-500 font-bold">{feedbackMsg}</span>}</span>
                    </div>

                    {/* New Save Area */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{t('CloudDecks.label_save_current')}</label>
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                            {/* Current Preview */}
                            <div className="flex gap-1">
                                {currentDeckData.map((item, idx) => {
                                    const id = (item && typeof item === 'object') ? item.id : item;
                                    return <DeckIcon key={idx} id={id} />;
                                })}
                            </div>

                            {/* Input & Save */}
                            <div className="flex-1 w-full flex gap-2">
                                <input
                                    type="text"
                                    value={saveName}
                                    onChange={(e) => setSaveName(e.target.value)}
                                    placeholder={t('CloudDecks.placeholder_deck_name')}
                                    className="flex-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:border-cyan-500"
                                />
                                <button
                                    onClick={() => handleSave()}
                                    disabled={decks.length >= 50}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('CloudDecks.btn_save')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Deck List */}
                    <div className="max-h-[400px] overflow-y-auto">
                        {loading && <div className="p-8 text-center text-slate-500">{t('CloudDecks.loading')}</div>}

                        {!loading && decks.length === 0 && (
                            <div className="p-8 text-center text-slate-400 italic">{t('CloudDecks.empty_state')}</div>
                        )}

                        {!loading && decks.map((deck) => (
                            <div key={deck.id} className="p-3 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors flex flex-col md:flex-row items-center gap-3 justify-between group">

                                {/* Info + Preview */}
                                <div className="flex flex-col md:flex-row items-center gap-3 flex-1">
                                    <div className="flex gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                        {Array.isArray(deck.data) && deck.data.map((item, idx) => {
                                            const id = (item && typeof item === 'object') ? item.id : item;
                                            return <DeckIcon key={idx} id={id} />;
                                        })}
                                    </div>
                                    <div className="text-center md:text-left">
                                        <div className="font-bold text-slate-700 dark:text-slate-200 text-sm">{deck.name}</div>
                                        <div className="text-[10px] text-slate-400">{new Date(deck.updated_at).toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => onLoadDeck(deck.data)}
                                        className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors text-xs font-bold"
                                        title={t('CloudDecks.btn_load')}
                                    >
                                        {t('CloudDecks.btn_load')}
                                    </button>
                                    <button
                                        onClick={() => handleSave(deck.id)}
                                        className="p-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-800 transition-colors text-xs font-bold"
                                        title={t('CloudDecks.btn_overwrite')}
                                    >
                                        {t('CloudDecks.btn_overwrite')}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(deck)}
                                        className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                        title={t('CloudDecks.title_delete', { defaultValue: 'Borrar' })}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Custom Delete Confirmation Modal */}
                    {deleteCandidate && createPortal(
                        <div
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in zoom-in duration-200"
                            onClick={() => setDeleteCandidate(null)}
                        >
                            <div
                                className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-sm w-full border border-slate-200 dark:border-slate-600 relative overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Decorative Background Element */}
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>

                                <div className="text-center">
                                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-red-50 dark:ring-red-900/10">
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </div>

                                    <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">{t('CloudDecks.modal_delete_title', { defaultValue: 'Delete Deck?' })}</h3>
                                    <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
                                        {t('CloudDecks.modal_delete_desc', { name: deleteCandidate.name })}
                                    </p>

                                    {/* Deck Preview in Modal */}
                                    <div className="flex justify-center gap-2 mb-8 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
                                        {Array.isArray(deleteCandidate.data) && deleteCandidate.data.map((item, idx) => {
                                            const id = (item && typeof item === 'object') ? item.id : item;
                                            return <DeckIcon key={idx} id={id} />;
                                        })}
                                    </div>

                                    <div className="flex gap-3 justify-center">
                                        <button
                                            onClick={() => setDeleteCandidate(null)}
                                            className="px-5 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-bold text-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                        >
                                            {t('CloudDecks.btn_cancel', { defaultValue: 'Cancel' })}
                                        </button>
                                        <button
                                            onClick={confirmDelete}
                                            className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 shadow-lg shadow-red-500/30 transition-all transform hover:scale-105 active:scale-95"
                                        >
                                            {t('CloudDecks.btn_confirm_delete', { defaultValue: 'Delete' })}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
                </div>
            )}
        </div>
    );
};

export default CloudDecks;
